<template>
  <h1 v-if="isLoggedIn" class="title mr-[15px] mt-8 pb-6 md:mt-[45px] md:pb-[64px]">
    Hello, {{ user?.first_name }}!
  </h1>
  <div v-if="isLoggedIn" class="flex flex-col max-lg:justify-between md:flex-row">
    <div
      class="left_side-wrapper scrollbar-hidden flex-nowrap overflow-x-scroll md:mr-[19.18%] md:overflow-x-visible"
    >
      <button
        @click="newRide"
        type="button"
        class="summary_edit_button m-0 text-nowrap px-[22px] py-[8px] font-bold md:px-[16px] md:py-[8px]"
      >
        Book a New Ride <span class="account__button__disc"></span>
      </button>
      <button type="button" class="account__button active text-nowrap">
        Account Information <span class="account__button__disc"></span>
      </button>
      <router-link to="/account/ridehistory" class="account__button text-nowrap"
        >Ride History <span class="account__button__disc"></span
      ></router-link>
      <router-link
        v-if="isReqAgency || isAgency"
        to="/account/agencyinformation"
        class="account__button text-nowrap"
        >Agency information <span class="account__button__disc"></span
      ></router-link>
      <button v-if="!isMobile" @click="logOut" type="button" class="account__button text-[#878787]">
        Log Out
      </button>
    </div>
    <div v-if="isMobile" class="mt-[6px]">
      <button @click="logOut" type="button" class="account__button text-[#878787]">Log Out</button>
    </div>
    <div class="flex w-full flex-col-reverse xl:flex-row">
      <Form
        v-if="user && isUserReady"
        @submit="onSubmit"
        class="right_side-wrapper xl:mr-[45px] xl:min-w-[552px]"
        :validation-schema="accountInfoSchema"
        v-slot="{ errors }"
      >
        <div
          class="right_side w-full max-w-none rounded-[40px] bg-white px-4 py-6 dark:bg-[#333639] max-md:mb-0 md:p-8 lg:max-w-[552px]"
        >
          <p class="text-background mb-5 dark:text-white md:hidden">Account Data</p>

          <span class="account_label">First Name</span>
          <Field
            type="text"
            name="first_name"
            maxlength="50"
            class="input account_input"
            :class="{ error: errors.first_name, '!text-[#6c6d6f]': !isEditable }"
            v-model="user.first_name"
            placeholder="First Name*"
            @beforeinput="utils.isLetter($event)"
            :disabled="!isEditable"
          />
          <span class="account_label">Last Name</span>
          <Field
            type="text"
            name="last_name"
            maxlength="50"
            class="input account_input"
            :class="{ error: errors.last_name, '!text-[#6c6d6f]': !isEditable }"
            v-model="user.last_name"
            placeholder="Last Name*"
            @beforeinput="utils.isLetter($event)"
            :disabled="!isEditable"
          />
          <span class="account_label">E-mail Address</span>
          <Field
            name="email"
            type="email"
            class="input account_input"
            placeholder="E-mail Address"
            v-model="user.email"
            :class="{ error: errors.email, '!text-[#6c6d6f]': !isEditable }"
            :disabled="!isEditable"
          />
          <span class="account_label">Mobile Phone Number</span>
          <vue-tel-input
            v-model="user.phone"
            :defaultCountry="user.country_prefix"
            @onInput="onInputPhone"
            @country-changed="countryChanged"
            v-on:keypress="validPhone($event, country_prefix || user.country_prefix)"
            class="input account_input mb-8 px-4 py-0"
            :class="!isEditable ? 'disabled' : ''"
            :dropdownOptions="{
              showFlags: true,
              showDialCodeInList: true,
              showDialCodeInSelection: true
            }"
            v-bind="bindProps"
          ></vue-tel-input>
          <button
            type="button"
            v-if="!isEditable"
            class="summary_edit_button border-dark_main text-dark_main dark:text-main dark:border-main dark:hover:text-background w-full px-4 py-[18px] text-base/[17.6px] md:py-5 md:text-[18px]/[23.8px]"
            @click="openEdit"
          >
            Edit
          </button>
          <button
            :disabled="isSubmitting"
            type="submit"
            v-if="isEditable"
            class="summary_edit_button save w-full px-4 py-[18px] text-base/[17.6px] md:py-5 md:text-[18px]/[23.8px]"
          >
            <LoadingSpinner
              v-if="isSubmitting"
              class="size-[18px] animate-spin stroke-[#2B2D32] md:size-6"
            />
            <span v-else>Save</span>
          </button>
        </div>
      </Form>
      <div
        v-if="agencyInformation"
        class="border-1 border-main bg-main_05 mb-6 mt-6 flex max-h-[300px] w-full flex-col rounded-[40px] border p-8 md:mt-0 lg:max-w-[552px]"
      >
        <p
          class="text-main mb-3 flex items-center gap-x-3 text-[14px] leading-[25px] md:text-[18px]"
        >
          <info-icon class="fill-main" />
          Information
        </p>
        <p class="flex flex-col text-[12px] leading-[15px] text-[#878787] md:text-[14px]">
          <template v-if="agencyInformation && agencyInformation.isLink">
            <span>{{ agencyInformation.text }}</span>
            <span class="mt-[10px]"
              >{{ agencyInformation.textBefore }}
              <router-link
                :to="agencyInformation.to"
                class="cursor-pointer text-black dark:text-white"
              >
                {{ agencyInformation.linkText }}
              </router-link>
              {{ agencyInformation.textAfter }}</span
            >
          </template>
          <template v-else-if="agencyInformation">
            {{ agencyInformation.text }}
          </template>
        </p>
      </div>
    </div>
  </div>
  <h1 v-if="!isLoggedIn" class="title mr-[15px] mt-[5rem]">
    Please login to view account information
  </h1>
  <router-link v-if="!isLoggedIn" to="/account/signin" class="button mt-[15px] w-fit"
    >Sign in
  </router-link>
  <swipe-modal
    v-model="isModal"
    v-if="isMobile"
    contents-height="60vh"
    border-top-radius="16px"
    background-color="ffffff"
    tip-color="#CDCFD0"
  >
    <car-popup-content />
  </swipe-modal>
</template>

<script setup>
import { ref, inject, reactive, computed, onBeforeMount } from 'vue'
import swipeModal from '@takuma-ru/vue-swipe-modal'
import { useRouter } from 'vue-router'
import { AsYouType } from 'libphonenumber-js'
import { validPhone } from '@/plugins/validPhone'
import * as yup from 'yup'
import { Form, Field } from 'vee-validate'
import { useMobile } from '@/compose/ismobile'
import { useContactsStore, useUserStore } from '@/stores/user'
import { storeToRefs } from 'pinia'
import CarPopupContent from '@/components/features/car/CarPopupContent.vue'
import LoadingSpinner from '@/components/ui/icons/LoadingSpinner.vue'
import { useOrderStore, useRidesHistoryStore } from '@/stores'
import InfoIcon from '@/components/ui/icons/InfoIcon.vue'

const router = useRouter()
const ridesStore = useRidesHistoryStore()
const userStore = useUserStore()
const orderStore = useOrderStore()
const contactsStore = useContactsStore()
const { isLoggedIn, user } = storeToRefs(userStore)
const utils = inject('utils')
const isSubmitting = ref(false)
const isModal = ref(false)

const code = ref(null)
const country_prefix = ref(null)

const isReqAgency = computed(() => {
  return user.value?.type === 'request for Agency'
})

const isAgency = computed(() => {
  return user.value?.type === 'agency'
})

const agencyInformation = computed(() => {
  if (user.value.show_information === 3) {
    return {
      isLink: true,
      text: 'Your Agency status is currently under verification. While we review your request, you can continue using the platform without restrictions.',
      textBefore: 'Please click on the ',
      linkText: 'Agency Information',
      textAfter:
        ' link in the left menu and complete the data — it will speed up the approval process',
      to: '/account/agencyinformation'
    }
  }

  if (user.value.show_information === 1) {
    return {
      isLink: false,
      text: 'Your request for Agency status has been declined. However, you can still use the platform as a Private user.'
    }
  }

  if (user.value.type === 'request for Agency' && user.value.show_information === null) {
    return {
      isLink: false,
      text: 'Your Agency status is currently under verification. While we review your request, you can continue using the platform without restrictions.'
    }
  }

  if (user.value.show_information === 2 && user.value.type === 'request for Agency') {
    const balanceText =
      user.value.agency_balance > 0
        ? `Your current credits balance is ${user.value.agency_balance},`
        : ''

    const discountText =
      user.value.agency_discount > 0
        ? ` and you now receive an additional ${user.value.agency_discount}% discount.`
        : ''

    return {
      isLink: false,
      text: `Your Agency status has been approved! ${balanceText}${discountText}`
    }
  }

  if (
    user.value.type === 'agency' &&
    (user.value.show_information === null || user.value.show_information === 2)
  ) {
    const balanceText =
      user.value.agency_balance > 0
        ? `Your current credits balance is ${user.value.agency_balance},`
        : ''

    const discountText =
      user.value.agency_discount > 0
        ? ` and you now receive an additional ${user.value.agency_discount}% discount.`
        : ''

    return {
      isLink: false,
      text: `Your Agency status has been approved! ${balanceText}${discountText}`.trim()
    }
  }

  return null
})

const isUserReady = computed(() => {
  return !!(
    user.value &&
    typeof user.value.first_name === 'string' &&
    typeof user.value.last_name === 'string' &&
    typeof user.value.email === 'string' &&
    typeof user.value.phone === 'string'
  )
})

const newRide = () => {
  orderStore.$reset()
  orderStore.changeTypeOrder('NEW')
  router.push({ name: 'home' })
}

const countryChanged = (country) => {
  code.value = '+' + country.dialCode
  country_prefix.value = country.iso2
}

const phoneNationalNumber = ref('')

const onInputPhone = (number, phoneObject) => {
  user.value.phone = new AsYouType(phoneObject.country.iso2).input(number)
  phoneNationalNumber.value = phoneObject.nationalNumber || ''
  if (phoneObject?.country) {
    code.value = '+' + phoneObject.country.dialCode
    country_prefix.value = phoneObject.country.iso2
  }
}

const bindProps = reactive({
  enabledFlags: true,
  preferredCountries: ['us', 'it', 'au', 'uk', 'in', 'ca', 'il', 'sg', 'ae'],
  inputClasses: 'input'
})

let isEditable = ref(false)
const { isMobile } = useMobile()

function logOut() {
  ridesStore.$reset()
  userStore.preventLogout()
  setTimeout(function () {
    router.push('/account/signin')
  }, 100)
}

const openEdit = () => {
  isEditable.value = true
}
const onSubmit = (values) => {
  isSubmitting.value = true
  const data = {
    first_name: values.first_name,
    last_name: values.last_name,
    email: values.email,
    phone: phoneNationalNumber.value,
    user_id: user.value.id,
    code: code.value,
    country_prefix: country_prefix.value
  }

  userStore
    .editUserSubmit(data)
    .then((success) => {
      if (!success) return
      isEditable.value = false
      contactsStore.add({
        code: code.value,
        country_prefix: data.country_prefix,
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        phone: data.phone
      })
    })
    .finally(() => {
      isSubmitting.value = false
    })
}
const accountInfoSchema = yup.object({
  email: yup.string().required().email().max(230),
  first_name: yup.string().required().max(50),
  last_name: yup.string().required().max(50)
})
onBeforeMount(() => {
  userStore.getUserInfo().then(() => {
    if (user.value) {
      user.value.phone = String(user.value.phone || '')
      user.value.email = String(user.value.email || '')
      user.value.first_name = String(user.value.first_name || '')
      user.value.last_name = String(user.value.last_name || '')
    }
  })
})
</script>

<style scoped>
.scrollbar-hidden::-webkit-scrollbar {
  display: none;
}

.scrollbar-hidden {
  scrollbar-width: none;
}
</style>
